/**
 * Animation d'apparition progressive pour les galeries masonry
 * Utilise IntersectionObserver pour déclencher l'animation au scroll
 * 
 * @package g3w-theme
 * @author Gianito - G3W
 */

(function () {
    'use strict';

    // Configuration
    const ANIMATION_CLASS = 'g3w-gallery-reveal';
    const REVEAL_CLASS = 'reveal';
    const GALLERY_SELECTORS = [
        '.wp-block-gallery.is-style-masonry',
        '.wp-block-gallery.is-style-masonry-no-captions',
        '.wp-block-gallery.g3w-gallery-reveal'
    ];

    /**
 * Initialise l'animation pour une galerie
 * @param {Element} gallery - L'élément galerie
 */
    function initGalleryAnimation(gallery) {
        // Ajouter la classe d'animation si elle n'est pas déjà présente
        if (!gallery.classList.contains(ANIMATION_CLASS)) {
            gallery.classList.add(ANIMATION_CLASS);
        }

        const images = gallery.querySelectorAll('.wp-block-image');

        // Si aucune image trouvée, ne rien faire
        if (images.length === 0) {
            return;
        }

        let revealCounter = 0;
        const isMobile = window.innerWidth <= 768;

        // Créer l'observer pour chaque image
        const observer = new IntersectionObserver((entries) => {
            // Trier les entrées par leur position verticale pour révéler dans l'ordre de lecture
            const intersectingEntries = entries.filter(entry => entry.isIntersecting);

            intersectingEntries.sort((a, b) => {
                const rectA = a.boundingClientRect;
                const rectB = b.boundingClientRect;
                // Trier d'abord par ligne (top), puis par colonne (left)
                if (Math.abs(rectA.top - rectB.top) < 50) { // même ligne (tolérance de 50px)
                    return rectA.left - rectB.left;
                }
                return rectA.top - rectB.top;
            });

            intersectingEntries.forEach((entry, index) => {
                const imageElement = entry.target;

                // Calculer le délai basé sur l'ordre d'apparition
                let delay = 0;
                if (revealCounter >= 3) { // Après les 3 premières images
                    delay = isMobile ?
                        Math.min((revealCounter - 2) * 0.1, 0.3) :
                        Math.min((revealCounter - 2) * 0.15, 0.6);
                }

                // Appliquer le délai et révéler
                setTimeout(() => {
                    imageElement.classList.add(REVEAL_CLASS);
                }, delay * 1000);

                revealCounter++;

                // Ne plus observer cette image une fois révélée
                observer.unobserve(imageElement);
            });
        }, {
            root: null,
            rootMargin: '0px 0px -20px 0px', // Déclenche dès que l'élément commence à être visible
            threshold: 0.05
        });

        // Observer chaque image
        images.forEach(image => {
            observer.observe(image);
        });
    }

    /**
 * Initialise toutes les galeries masonry sur la page
 */
    function initGalleryAnimations() {
        GALLERY_SELECTORS.forEach(selector => {
            const galleries = document.querySelectorAll(selector);

            galleries.forEach(gallery => {
                // Vérifier si la galerie n'a pas déjà été traitée
                if (!gallery.hasAttribute('data-g3w-reveal-initialized')) {
                    initGalleryAnimation(gallery);
                    gallery.setAttribute('data-g3w-reveal-initialized', 'true');
                }
            });
        });
    }

    /**
     * Réinitialise les animations (utile pour le contenu dynamique)
     */
    function resetGalleryAnimations() {
        const galleries = document.querySelectorAll(`.${ANIMATION_CLASS}`);

        galleries.forEach(gallery => {
            const images = gallery.querySelectorAll('.wp-block-image');
            images.forEach(image => {
                image.classList.remove(REVEAL_CLASS);
            });
            gallery.classList.remove(ANIMATION_CLASS);
        });

        // Réinitialiser
        initGalleryAnimations();
    }

    /**
     * Vérifie si les animations sont supportées
     * @returns {boolean}
     */
    function isAnimationSupported() {
        return 'IntersectionObserver' in window &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /**
     * Initialisation principale
     */
    function init() {
        // Vérifier le support des animations
        if (!isAnimationSupported()) {
            // Si pas de support ou préférence pour animations réduites,
            // on ajoute quand même la classe pour le CSS de base
            GALLERY_SELECTORS.forEach(selector => {
                const galleries = document.querySelectorAll(selector);
                galleries.forEach(gallery => {
                    gallery.classList.add(ANIMATION_CLASS);
                    const images = gallery.querySelectorAll('.wp-block-image');
                    images.forEach(image => {
                        image.classList.add(REVEAL_CLASS);
                    });
                });
            });
            return;
        }

        initGalleryAnimations();

        // Écouter les changements de contenu dynamique (AJAX, etc.)
        if ('MutationObserver' in window) {
            const contentObserver = new MutationObserver((mutations) => {
                let shouldReinit = false;

                mutations.forEach(mutation => {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        mutation.addedNodes.forEach(node => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                // Vérifier si de nouvelles galeries ont été ajoutées
                                const hasNewGalleries = GALLERY_SELECTORS.some(selector =>
                                    node.matches && node.matches(selector) ||
                                    node.querySelector && node.querySelector(selector)
                                );

                                if (hasNewGalleries) {
                                    shouldReinit = true;
                                }
                            }
                        });
                    }
                });

                if (shouldReinit) {
                    setTimeout(initGalleryAnimations, 100); // Petit délai pour que le DOM se stabilise
                }
            });

            contentObserver.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
    }

    // Initialiser quand le DOM est prêt
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Exposer des méthodes utiles pour le debugging ou usage avancé
    window.G3WGalleryReveal = {
        init: initGalleryAnimations,
        reset: resetGalleryAnimations
    };

})(); 